// Every rate on the wire is a fraction as a decimal string ("0.001" is 0.1 %).
// The shift is done on the string so a rate like "0.0007" never picks up the
// float artifacts of `0.0007 * 100`.
const shiftDecimalPoint = (value: string, places: number) => {
  const sign = value.startsWith('-') ? '-' : '';
  const [intPart, fracPart = ''] = value.replace(/^[+-]/, '').split('.');
  let digits = (intPart || '0') + fracPart;
  let point = (intPart || '0').length + places;
  while (point < 1) {
    digits = `0${digits}`;
    point += 1;
  }
  while (point > digits.length) {
    digits = `${digits}0`;
  }
  const head = digits.slice(0, point).replace(/^0+(?=\d)/, '');
  const tail = digits.slice(point).replace(/0+$/, '');
  return `${sign}${head}${tail ? `.${tail}` : ''}`;
};

const isNumeric = (value?: string | null): value is string =>
  typeof value === 'string' && value.trim() !== '' && !Number.isNaN(+value);

/** Fraction as stored (`"0.001"`) to the percent shown on screen (`"0.1"`). */
export const rateToPercent = (rate?: string | null) =>
  isNumeric(rate) ? shiftDecimalPoint(rate.trim(), 2) : undefined;

/** Percent typed by an admin (`"0.1"`) back to the fraction the API takes. */
export const percentToRate = (percent?: string | null) =>
  isNumeric(percent) ? shiftDecimalPoint(percent.trim(), -2) : undefined;

/** The API rejects anything outside 0 … 0.1, so the inputs cap at 10 %. */
export const MAX_RATE_PERCENT = '10';
export const RATE_PERCENT_PRECISION = 4;

/**
 * The spread of one rate across every pair. `varies` is false when a tier
 * charges the same everywhere, which is the common case — a tier only differs
 * per pair where an admin has added an override.
 */
export const rateSpread = (values: (string | undefined | null)[]) => {
  const numeric = values.filter(isNumeric);
  if (!numeric.length) return undefined;
  let min = numeric[0];
  let max = numeric[0];
  for (const value of numeric) {
    if (+value < +min) min = value;
    if (+value > +max) max = value;
  }
  return { min, max, varies: +min !== +max };
};
